import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Spacing } from '@/shared/theme';
import { Button, Title, Text } from '@/shared/ui';
import { Modal } from '@/shared/Modal';
import type { TrackType } from '@/types/registration';

interface LearnMoreModalProps {
  isOpen: boolean;
  onClose: () => void;
  eligibleTracks: TrackType[];
}

const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacing.x3}px;
  width: 100%;
`;

const ProgramSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacing.x1}px;
`;

const ProgramsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacing.x2}px;
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  padding-top: ${Spacing.x3}px;

  > button {
    max-width: 343px;
  }
`;

const LearnMoreModal: React.FC<LearnMoreModalProps> = ({
  isOpen,
  onClose,
  eligibleTracks,
}) => {
  const { t } = useTranslation();

  const handleClose = () => {
    onClose();
  };

  const handleGotIt = () => {
    onClose();
  };

  // Map of track types to translation keys
  const trackInfo = {
    eCKM: {
      titleKey: 'modals.learn-more.eckm-title',
      descriptionKey: 'modals.learn-more.eckm-description',
    },
    CKM: {
      titleKey: 'modals.learn-more.ckm-title',
      descriptionKey: 'modals.learn-more.ckm-description',
    },
    BH: {
      titleKey: 'modals.learn-more.bh-title',
      descriptionKey: 'modals.learn-more.bh-description',
    },
  };

  return (
    <Modal
      open={isOpen}
      onClose={handleClose}
      ariaLabelledBy="learn-more-modal-title"
      maxWidth={600}
      footer={
        <ButtonContainer>
          <Button
            text={t('modals.learn-more.cta')}
            onClick={handleGotIt}
            variant="action-primary"
          />
        </ButtonContainer>
      }
    >
      <ModalContent>
        <Title size="h3" bold id="learn-more-modal-title">
          {t('modals.learn-more.title')}
        </Title>

        <ProgramsContainer>
          {eligibleTracks.map(track => (
            <ProgramSection key={track}>
              <Title size="h5" bold>
                {t(trackInfo[track].titleKey)}
              </Title>
              <Text size="p1">{t(trackInfo[track].descriptionKey)}</Text>
            </ProgramSection>
          ))}
        </ProgramsContainer>
      </ModalContent>
    </Modal>
  );
};

export { LearnMoreModal };
